import { useCallback, useEffect, useState } from "react";
import api, { runJob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  FileText, Newspaper, Tag, Code2, Image as ImageIcon, Sparkles, Loader2, Copy, Trash2, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/brand";

const TYPES = [
  { id: "new_pages", label: "New Pages", icon: FileText, desc: "SEO pages to build" },
  { id: "blog", label: "Blog Post", icon: Newspaper, desc: "Full article" },
  { id: "keywords", label: "Keyword Strategy", icon: Tag, desc: "Local + gap keywords" },
  { id: "meta", label: "Page Metadata", icon: Code2, desc: "Titles, H1, CTA" },
  { id: "image_seo", label: "Image SEO", icon: ImageIcon, desc: "Filenames + alt text" },
];

const copy = (txt) => { navigator.clipboard.writeText(typeof txt === "string" ? txt : JSON.stringify(txt, null, 2)); toast.success("Copied"); };
const Chips = ({ items, color = "bg-slate-100 text-slate-600" }) => (
  <div className="flex flex-wrap gap-1.5">{(items || []).map((k, i) => <span key={i} className={`text-[11px] px-2 py-0.5 rounded ${color}`}>{k}</span>)}</div>
);

function Result({ type, data }) {
  if (!data) return null;
  if (type === "keywords") {
    const groups = [["Primary", data.primary, "bg-orange-50 text-orange-700"], ["Local", data.local, "bg-emerald-50 text-emerald-700"],
      ["Long-tail", data.long_tail, "bg-blue-50 text-blue-700"], ["Competitor gap", data.competitor_gap, "bg-violet-50 text-violet-700"],
      ["Questions", data.questions, "bg-slate-100 text-slate-600"]];
    return <div className="space-y-3">{groups.map(([t, arr, c]) => arr?.length ? (
      <div key={t}><p className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">{t}</p><Chips items={arr} color={c} /></div>) : null)}</div>;
  }
  if (type === "blog") {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <p><span className="text-slate-400 text-xs">Meta title:</span> {data.meta_title}</p>
          <p><span className="text-slate-400 text-xs">Slug:</span> /{data.slug}</p>
          <p className="md:col-span-2"><span className="text-slate-400 text-xs">Meta description:</span> {data.meta_description}</p>
          <p className="md:col-span-2"><span className="text-slate-400 text-xs">H1:</span> {data.h1}</p>
          <p className="md:col-span-2"><span className="text-slate-400 text-xs">CTA:</span> {data.cta}</p>
        </div>
        {data.target_keywords && <Chips items={data.target_keywords} color="bg-orange-50 text-orange-700" />}
        {data.image_names && <div><p className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Image names</p><Chips items={data.image_names} /></div>}
        <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 whitespace-pre-wrap max-h-96 overflow-y-auto scroll-thin">{data.body_markdown}</div>
        <Button variant="outline" size="sm" onClick={() => copy(data.body_markdown)}><Copy size={13} className="mr-1.5" /> Copy article</Button>
      </div>
    );
  }
  if (type === "image_seo") {
    return <div className="rounded-md border border-slate-200 divide-y">{(data.images || []).map((im, i) => (
      <div key={i} className="px-3 py-2 text-sm"><p className="font-mono text-[13px] text-slate-800">{im.suggested_filename}</p>
        <p className="text-xs text-slate-500">alt: {im.alt_text} · {im.page}</p></div>))}</div>;
  }
  // new_pages / meta -> items
  const items = data.items || [];
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{items.map((it, i) => (
    <div key={i} className="rounded-md border border-slate-200 p-3.5 space-y-1.5" data-testid="content-item-card">
      <p className="font-display font-semibold text-slate-900 text-sm">{it.title || it.h1 || it.page}</p>
      {it.slug && <p className="text-[11px] text-slate-400">/{it.slug}</p>}
      {it.meta_title && <p className="text-[13px]"><span className="text-slate-400 text-xs">Title:</span> {it.meta_title}</p>}
      {it.meta_description && <p className="text-[13px] text-slate-600">{it.meta_description}</p>}
      {it.h1 && <p className="text-[13px]"><span className="text-slate-400 text-xs">H1:</span> {it.h1}</p>}
      {it.headline && <p className="text-[13px]"><span className="text-slate-400 text-xs">Headline:</span> {it.headline}</p>}
      {it.cta && <p className="text-[13px]"><span className="text-slate-400 text-xs">CTA:</span> {it.cta}</p>}
      {it.target_keywords && <Chips items={it.target_keywords} color="bg-orange-50 text-orange-700" />}
      {it.outline && <ul className="text-[12px] text-slate-500 list-disc pl-4">{it.outline.map((o, j) => <li key={j}>{o}</li>)}</ul>}
      {it.image_names && <Chips items={it.image_names} />}
    </div>))}</div>;
}

export default function ContentStudio() {
  const { user } = useAuth();
  const isStaff = user.role !== "client";
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(isStaff ? "" : user.client_id);
  const [type, setType] = useState("new_pages");
  const [topic, setTopic] = useState("");
  const [keywords, setKeywords] = useState("");
  const [loading, setLoading] = useState(false);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [language, setLanguage] = useState("English");
  const [ideas, setIdeas] = useState({ keywords: [], blogs: [] });

  useEffect(() => {
    if (isStaff) api.get("/clients").then((r) => { setClients(r.data); if (r.data[0]) setClientId(r.data[0].id); });
  }, [isStaff]);

  const loadHistory = useCallback((cid) => { if (cid) api.get(`/clients/${cid}/content`).then((r) => setHistory(r.data)); }, []);
  useEffect(() => {
    loadHistory(clientId);
    setCurrent(null);
    if (clientId) api.get(`/clients/${clientId}/competitors`).then((r) => {
      const kws = [], bl = [];
      r.data.forEach((c) => { const a = c.analysis || {}; (a.keyword_ideas || []).forEach((k) => kws.push(k)); (a.blog_ideas || []).forEach((b) => bl.push(b)); });
      setIdeas({ keywords: [...new Set(kws)].slice(0, 18), blogs: [...new Set(bl)].slice(0, 10) });
    }).catch(() => {});
  }, [clientId, loadHistory]);

  const generate = async (ov = {}) => {
    if (!clientId) return;
    const payload = { type: ov.type || type, topic: ov.topic ?? topic, keywords: ov.keywords ?? keywords, language };
    if (ov.type) setType(ov.type);
    setLoading(true); setCurrent(null);
    try {
      const result = await runJob(api.post(`/clients/${clientId}/content/generate`, payload));
      setCurrent(result);
      loadHistory(clientId);
    } catch (e) { toast.error(e.message || "Generation failed"); }
    finally { setLoading(false); }
  };

  const del = async (id) => { await api.delete(`/content/${id}`); loadHistory(clientId); };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">AI Content Studio</h1>
          <p className="text-slate-500 text-sm mt-1">Generate SEO pages, blogs, keywords & metadata for your industry & local market</p>
        </div>
        {isStaff && (
          <Select value={clientId} onValueChange={setClientId}>
            <SelectTrigger className="w-48" data-testid="content-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      <Card className="p-5 border-slate-200">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 mb-4">
          {TYPES.map((t) => (
            <button key={t.id} onClick={() => setType(t.id)} data-testid={`type-${t.id}`}
              className={`rounded-md border p-3 text-left transition-all ${type === t.id ? "border-orange-400 bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
              <t.icon size={18} style={{ color: type === t.id ? "#F7941E" : "#64748b" }} />
              <p className="text-sm font-medium text-slate-800 mt-1.5">{t.label}</p>
              <p className="text-[11px] text-slate-400">{t.desc}</p>
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div><Label className="text-xs uppercase tracking-wider text-slate-500">Topic (optional — auto if blank)</Label>
            <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. truck accident claims" className="mt-1.5" data-testid="content-topic-input" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-slate-500">Focus keywords (optional)</Label>
            <Input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="comma separated" className="mt-1.5" data-testid="content-keywords-input" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-slate-500">Language</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="mt-1.5" data-testid="content-language-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["English", "Spanish", "French", "Portuguese", "German"].map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select></div>
        </div>
        <Button onClick={() => generate()} disabled={loading || !clientId} className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="generate-content-btn">
          {loading ? <><Loader2 size={16} className="animate-spin mr-1.5" /> Generating…</> : <><Sparkles size={16} className="mr-1.5" /> Generate {language !== "English" ? `(${language})` : ""}</>}
        </Button>
        {(ideas.blogs.length > 0 || ideas.keywords.length > 0) && (
          <div className="mt-5 border-t border-slate-100 pt-4 space-y-3">
            <p className="text-xs uppercase tracking-wider text-slate-400">From competitor research — click to generate</p>
            {ideas.blogs.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {ideas.blogs.map((b, i) => (
                  <button key={i} onClick={() => generate({ type: "blog", topic: b })} data-testid="idea-blog-chip"
                    className="text-[11px] px-2 py-1 rounded bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors text-left">📝 {b}</button>
                ))}
              </div>
            )}
            {ideas.keywords.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {ideas.keywords.map((k, i) => (
                  <button key={i} onClick={() => setKeywords(k)} data-testid="idea-kw-chip"
                    className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors">{k}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {current && (
        <Card className="p-5 border-slate-200" data-testid="content-result">
          <div className="flex items-center justify-between mb-4">
            <p className="font-display font-semibold text-slate-900">{TYPES.find((t) => t.id === current.type)?.label} result</p>
            <Button variant="ghost" size="sm" onClick={() => copy(current.data)}><Copy size={14} className="mr-1.5" /> Copy JSON</Button>
          </div>
          <Result type={current.type} data={current.data} />
        </Card>
      )}

      {history.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Saved generations</p>
          <div className="space-y-2">
            {history.map((h) => (
              <Card key={h.id} className="border-slate-200" data-testid="history-item">
                <button onClick={() => setOpenId(openId === h.id ? null : h.id)} className="w-full flex items-center gap-3 px-4 py-3 text-left">
                  <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{TYPES.find((t) => t.id === h.type)?.label || h.type}</span>
                  <span className="text-sm text-slate-700 truncate flex-1">{h.topic || "—"}</span>
                  <span className="text-[11px] text-slate-400">{fmtDate(h.created_at)} · {h.created_by}</span>
                  <Trash2 size={14} className="text-red-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); del(h.id); }} data-testid="del-content-btn" />
                  <ChevronDown size={15} className={`text-slate-400 transition-transform ${openId === h.id ? "rotate-180" : ""}`} />
                </button>
                {openId === h.id && <div className="px-4 pb-4"><Result type={h.type} data={h.data} /></div>}
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
